import type { Address } from "viem"
import { readContract } from "@wagmi/core"
import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { ApprovalMode, ContractRiskLevel } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenStore } from "@/domains/tokens"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { erc20Abi } from "@/domains/abis"
import { determineApprovalAmount, unixTimestamp } from "@/domains/library"
import { OrchestratedQuery } from "@/domains/library/orchestrated-query"
import { flyTransactionResponseSchema } from "@/domains/validators"

import type {
  IAggregatorApiTransport,
  IAggregatorOnChainTransport,
  IPriceImpactCalculator,
  ISwapAggregatorPlugin,
  ISwapAggregatorSettingsStore,
  ISwapCoordinator,
  ISwapInputStore,
} from "../interfaces"
import type {
  AggregatorQuoteRequest,
  AggregatorTransactionRequest,
  NormalizedSwapQuote,
  NormalizedSwapTransaction,
  SwapQuoteRequestData,
} from "../types"
import {
  SwapAggregatorDisabledEvent,
  SwapAggregatorEnabledEvent,
  SwapApprovalCompletedEvent,
  SwapApprovalStartedEvent,
  SwapErrorEvent,
  SwapQuoteReceivedEvent,
  SwapQuoteRequestClearedEvent,
  SwapQuoteRequestedEvent,
} from "../events"
import { FlyApiTransport } from "../transports/fly-api-transport"

type FlyConfig = {
  quoteAPI: string
  transactionAPI: string
  nativeAddress: string
  network: string
}

const BASE_CONFIG: FlyConfig = {
  quoteAPI: "https://api.fly.trade/aggregator/quote",
  transactionAPI: "https://api.fly.trade/aggregator/transaction",
  nativeAddress: "0x0000000000000000000000000000000000000000",
  network: "base",
}

const getFlyChainConfig = (chainId: number): FlyConfig => {
  switch (chainId) {
    case 8453:
      return BASE_CONFIG
    default:
      throw new Error(`Chain ${chainId} not supported by Fly`)
  }
}

/**
 * Fly Swap Plugin
 * Implements ISwapAggregatorPlugin for Fly (formerly Magpie) aggregator
 * Each aggregator is a self-contained mini-domain that owns its quotes
 */
export class FlySwapPlugin implements ISwapAggregatorPlugin {
  // Static metadata
  readonly id = "fly"
  readonly name = "Fly"
  readonly displayName = "Fly"
  readonly description = "Fast and secure cross-chain bridge aggregator"
  readonly icon = "/brands/fly-icon.svg"
  readonly color = "from-pink-500 to-rose-500"
  readonly isProductionReady = true

  // Quote state (owned by aggregator)
  latestQuote: NormalizedSwapQuote | null = null
  selectedQuote: NormalizedSwapQuote | null = null

  // Quote request state (stored from events)
  private currentQuoteRequest: SwapQuoteRequestData | null = null

  // Internal state
  private quoteQuery: OrchestratedQuery<void> | null = null
  private apiTransport: IAggregatorApiTransport | null = null
  private initialized = false

  // Dependencies (set during initialize)
  private orchestratedQueryStore: OrchestratedQueryStore | null = null
  private eventBus: IEventBus | null = null
  private swapCoordinator: ISwapCoordinator | null = null
  private inputStore: ISwapInputStore | null = null
  private priceImpactCalculator: IPriceImpactCalculator | null = null
  private chainId: number | null = null
  private nativeToken: TokenStore | null = null
  private walletStore: IWalletConnectionStore | null = null
  private slippageBps: number | null = null
  private settingsStore: ISwapAggregatorSettingsStore | null = null
  private config: any = null // Wagmi config for readContract

  constructor(
    private readonly priceImpactCalculatorParam: IPriceImpactCalculator,
    private readonly getConfig: () => any, // Wagmi config getter
  ) {
    makeObservable(this, {
      latestQuote: observable,
      selectedQuote: observable,
      selectCurrentQuote: action,
      updateSelectedQuote: action,
      clearSelection: action,
      displayQuote: computed,
      hasBetterQuote: computed,
      isLoadingQuotes: computed,
    })
  }

  /**
   * Check if the plugin's quote query is currently running
   */
  get isLoadingQuotes(): boolean {
    return this.quoteQuery?.isRunning ?? false
  }

  /**
   * Initialize the aggregator with its own orchestrator and query cycle
   */
  initialize(
    orchestratedQueryStore: OrchestratedQueryStore,
    eventBus: IEventBus,
    swapCoordinator: ISwapCoordinator,
    inputStore: ISwapInputStore,
    priceImpactCalculator: IPriceImpactCalculator,
    chainId: number,
    nativeToken: TokenStore,
    walletStore: IWalletConnectionStore,
    slippageBps: number,
    settingsStore: ISwapAggregatorSettingsStore,
    apiTransport?: IAggregatorApiTransport,
    _onChainTransport?: IAggregatorOnChainTransport,
  ): void {
    if (this.initialized) {
      throw new Error("Fly plugin already initialized")
    }

    this.orchestratedQueryStore = orchestratedQueryStore
    this.eventBus = eventBus
    this.swapCoordinator = swapCoordinator
    this.inputStore = inputStore
    this.priceImpactCalculator = priceImpactCalculator
    this.chainId = chainId
    this.nativeToken = nativeToken
    this.walletStore = walletStore
    this.slippageBps = slippageBps
    this.settingsStore = settingsStore
    this.config = this.getConfig()

    // Use provided transport or create default
    this.apiTransport = apiTransport ?? new FlyApiTransport()

    // Create quote query with Fly-specific interval (10 seconds)
    const queryName = `Fly Quotes`
    this.quoteQuery = new OrchestratedQuery<void>(
      queryName,
      (_query) => this.fetchQuoteInternal(), // Query parameter available but not used (no chunking)
      10_000, // 10 seconds
      this.onQuoteSuccess.bind(this),
      this.onQuoteFailed.bind(this),
      orchestratedQueryStore,
    )

    // Register query with coordinator
    swapCoordinator.registerQuoteQuery(queryName)

    // Subscribe to quote request events
    eventBus.subscribe(SwapQuoteRequestedEvent.TYPE, this.handleQuoteRequested.bind(this))
    eventBus.subscribe(SwapQuoteRequestClearedEvent.TYPE, this.handleQuoteRequestCleared.bind(this))
    eventBus.subscribe(SwapAggregatorEnabledEvent.TYPE, this.handleAggregatorEnabled.bind(this))
    eventBus.subscribe(SwapAggregatorDisabledEvent.TYPE, this.handleAggregatorDisabled.bind(this))

    // Do NOT start query during initialization - wait for events
    this.initialized = true
  }

  /**
   * Check if aggregator is enabled
   */
  private isEnabled(): boolean {
    if (!this.settingsStore) return false
    const aggregator = this.settingsStore.SwapAggregators.find(this.id)
    if (this.settingsStore.SwapAggregators.fallbackMode === "multi") {
      return Boolean(aggregator?.enabled)
    }
    return aggregator?.enabled || this.settingsStore.SwapAggregators.activeFallbackAggregatorId === this.id || false
  }

  /**
   * Internal quote fetching (called by OrchestratedQuery)
   */
  private async fetchQuoteInternal(): Promise<void> {
    if (!this.walletStore || !this.apiTransport || !this.currentQuoteRequest) {
      return
    }

    // Get user address dynamically
    const userAddress = this.walletStore.safeLensAddress

    const { inputToken, outputToken, inputAmount, slippageBps, chainId, nativeToken } = this.currentQuoteRequest

    if (!inputToken || !outputToken || !inputAmount || inputAmount === "0") {
      return
    }

    try {
      // Validate amount format (matching old implementation)
      if (inputAmount === "." || inputAmount.endsWith(".")) {
        return
      }

      // Parse amount using token's parseUnits method (tied to token decimals)
      let inputAmountRaw: bigint
      try {
        inputAmountRaw = inputToken.parseUnits(inputAmount)
      } catch (e) {
        // Invalid amount format
        return
      }

      // Validate amount string length (matching old implementation)
      const amountStr = inputAmountRaw.toString().replace(/n$/, "")
      if (amountStr.length > 160) {
        return
      }

      // Build request
      const request: AggregatorQuoteRequest = {
        chainId,
        inputToken,
        outputToken,
        inputAmountRaw,
        inputAmountFormatted: inputAmount,
        slippageBps,
        userAddress,
        nativeToken,
      }

      // Fetch quote using transport
      const rawQuote = await this.apiTransport.fetchQuote(request)

      // Normalize quote
      const normalizedQuote = await this.normalizeQuote(rawQuote, request)

      if (normalizedQuote) {
        runInAction(() => {
          this.latestQuote = normalizedQuote
        })

        // Publish event
        if (this.eventBus) {
          this.eventBus.publish(new SwapQuoteReceivedEvent(normalizedQuote))
        }
      }
    } catch (error) {
      console.error(`[Fly] Error fetching quote:`, error)
    }
  }

  /**
   * Normalize raw quote response to NormalizedSwapQuote
   */
  private async normalizeQuote(
    rawQuote: unknown,
    request: AggregatorQuoteRequest,
  ): Promise<NormalizedSwapQuote | null> {
    try {
      // Extract quote data (rawQuote is validated by transport)
      const quoteData = rawQuote as any

      // Get quote-id
      const quoteId = quoteData["quote-id"] ?? quoteData.id
      if (!quoteId) {
        return null
      }

      // Parse output amount
      const outputAmountStr = quoteData.toTokenAmount ?? quoteData.amountOut
      if (!outputAmountStr) {
        return null
      }

      const outputAmountRaw = BigInt(outputAmountStr)

      // Calculate exchange rate
      const exchangeRate =
        request.inputAmountRaw > 0n ? (Number(outputAmountRaw) / Number(request.inputAmountRaw)).toString() : "0"

      // Calculate price impact
      const inputTokenPrice = request.inputToken.bestPrice
      const outputTokenPrice = request.outputToken.bestPrice
      const inputAmountUsd = Number(request.inputAmountFormatted) * inputTokenPrice
      const outputAmountFormatted = request.outputToken.formatUnits(outputAmountRaw)
      const outputAmountUsd = Number(outputAmountFormatted) * outputTokenPrice

      const priceImpactBps = this.priceImpactCalculatorParam.calculate(inputAmountUsd, outputAmountUsd)

      // Get gas estimate
      const gasLimit = quoteData.resourceEstimate?.gasLimit
        ? BigInt(quoteData.resourceEstimate.gasLimit)
        : BigInt(500000)

      const flyConfig = getFlyChainConfig(request.chainId)

      // Build normalized quote
      const normalizedQuote: NormalizedSwapQuote = {
        aggregatorId: this.id,
        quoteId,
        inputToken: request.inputToken,
        outputToken: request.outputToken,
        inputAmountRaw: request.inputAmountRaw,
        outputAmountRaw,
        inputAmountFormatted: request.inputAmountFormatted,
        outputAmountFormatted,
        exchangeRate,
        priceImpactBps,
        gasEstimate: {
          gasLimit,
        },
        transactionData: null, // Fly doesn't include transaction data in quote
        transactionMetadata: {
          quoteId,
          network: flyConfig.network,
          targetAddress: quoteData.targetAddress,
        },
        timestamp: unixTimestamp(),
      }

      return normalizedQuote
    } catch (error) {
      console.error("Error normalizing Fly quote:", error)
      return null
    }
  }

  /**
   * Quote query success handler
   */
  private onQuoteSuccess(_sender: IOrchestratedQuerySender<void>, _data: void): void {
    // Quote already updated in fetchQuoteInternal
    this.quoteQuery?.stop()
  }

  /**
   * Quote query failure handler
   */
  private onQuoteFailed(_sender: IOrchestratedQuerySender<void>, _error: Error): void {
    this.quoteQuery?.stop()
    console.error("Fly quote query failed:", _error)
  }

  /**
   * Fetch quote (public API - updates latestQuote internally)
   */
  async fetchQuote(params: AggregatorQuoteRequest): Promise<void> {
    if (!this.apiTransport) {
      throw new Error("Fly plugin not initialized")
    }

    try {
      const rawQuote = await this.apiTransport.fetchQuote(params)
      const normalizedQuote = await this.normalizeQuote(rawQuote, params)

      if (normalizedQuote) {
        runInAction(() => {
          this.latestQuote = normalizedQuote
        })

        if (this.eventBus) {
          this.eventBus.publish(new SwapQuoteReceivedEvent(normalizedQuote))
        }
      }
    } catch (error) {
      console.error("Error fetching Fly quote:", error)
      throw error
    }
  }

  /**
   * Build transaction data
   */
  async buildTransaction(
    quote: NormalizedSwapQuote,
    params: AggregatorTransactionRequest,
  ): Promise<NormalizedSwapTransaction | null> {
    if (!this.apiTransport) {
      return null
    }

    try {
      const quoteId = quote.transactionMetadata?.quoteId as string | undefined
      if (!quoteId) {
        return null
      }

      // Fetch transaction using transport
      const rawTransaction = await this.apiTransport.buildTransaction(quoteId, params)

      // Validate and normalize
      const parsedTransaction = flyTransactionResponseSchema.safeParse(rawTransaction)
      if (!parsedTransaction.success) {
        console.error("Fly transaction response validation failed:", parsedTransaction.error)
        return null
      }

      const flyTransaction = parsedTransaction.data

      // Ensure data starts with 0x
      const data = flyTransaction.data.startsWith("0x")
        ? (flyTransaction.data as `0x${string}`)
        : (`0x${flyTransaction.data}` as `0x${string}`)

      if (data === "0x") {
        return null
      }

      // Get gas limit
      const gasLimit = flyTransaction.gasLimit
        ? BigInt(flyTransaction.gasLimit)
        : flyTransaction.gas
          ? BigInt(flyTransaction.gas)
          : BigInt(500000)

      // Build normalized transaction
      const normalizedTransaction: NormalizedSwapTransaction = {
        to: flyTransaction.to as `0x${string}`,
        data,
        value: flyTransaction.value ? BigInt(flyTransaction.value) : 0n,
        gasLimit,
      }

      return normalizedTransaction
    } catch (error) {
      console.error("Error building Fly transaction:", error)
      return null
    }
  }

  supportsDirectTransaction(): boolean {
    return false // Requires separate transaction API call
  }

  supportsApiTransport(): boolean {
    return true
  }

  supportsOnChainTransport(): boolean {
    return false // Not implemented yet
  }

  /**
   * Quote management methods
   */
  selectCurrentQuote(): void {
    if (this.latestQuote) {
      this.selectedQuote = this.latestQuote
    }
  }

  updateSelectedQuote(quote: NormalizedSwapQuote): void {
    this.selectedQuote = quote
  }

  clearSelection(): void {
    this.selectedQuote = null
  }

  clearQuotes(): void {
    runInAction(() => {
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  /**
   * Event handlers for quote request events
   */
  private handleQuoteRequested(event: SwapQuoteRequestedEvent): void {
    // Store the quote request state
    runInAction(() => {
      this.currentQuoteRequest = event.quoteRequest
    })

    // If plugin is enabled, start query and force execute immediately
    if (this.isEnabled() && this.quoteQuery) {
      if (!this.quoteQuery.isActive) {
        this.quoteQuery.start()
      } else if (!this.quoteQuery.isRunning) {
        this.quoteQuery.forceExecute()
      }
    }
  }

  private handleQuoteRequestCleared(_event: SwapQuoteRequestClearedEvent): void {
    // Stop query if active
    if (this.quoteQuery?.isActive) {
      this.quoteQuery.stop()
    }

    // Clear stored state and quotes
    runInAction(() => {
      this.currentQuoteRequest = null
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  private handleAggregatorEnabled(event: SwapAggregatorEnabledEvent): void {
    // Only react if this event is for this plugin
    if (event.aggregatorId !== this.id) {
      return
    }

    // Check if we have a valid stored quote request
    if (this.currentQuoteRequest && this.quoteQuery) {
      // Start query and force execute immediately
      if (!this.quoteQuery.isActive) {
        this.quoteQuery.start()
      } else if (!this.quoteQuery.isRunning) {
        this.quoteQuery.forceExecute()
      }
    }
  }

  private handleAggregatorDisabled(event: SwapAggregatorDisabledEvent): void {
    // Only react if this event is for this plugin
    if (event.aggregatorId !== this.id) {
      return
    }

    // Stop query but keep stored state (user might re-enable later)
    if (this.quoteQuery?.isActive) {
      this.quoteQuery.stop()
    }
  }

  get displayQuote(): NormalizedSwapQuote | null {
    return this.selectedQuote ?? this.latestQuote
  }

  get hasBetterQuote(): boolean {
    if (!this.selectedQuote || !this.latestQuote) {
      return false
    }

    // Compare output amounts - better quote has higher output
    return this.latestQuote.outputAmountRaw > this.selectedQuote.outputAmountRaw
  }

  /**
   * Execution methods
   */
  async getAllowance(token: TokenStore, spender: Address, userAddress: Address): Promise<bigint> {
    if (!this.config || !this.chainId) {
      return 0n
    }

    try {
      const result = await readContract(this.config, {
        address: token.address.toViemAddress(),
        abi: erc20Abi,
        functionName: "allowance",
        args: [userAddress, spender],
        chainId: this.chainId,
      })

      return result as bigint
    } catch (error) {
      console.error("Error getting allowance:", error)
      return 0n
    }
  }

  async executeAllowanceTransaction(
    quote: NormalizedSwapQuote,
    currentAllowance: bigint,
    userAddress: Address,
    approvalMode: ApprovalMode,
    contractRiskLevel: ContractRiskLevel,
  ): Promise<NormalizedSwapTransaction | null> {
    if (!this.eventBus) {
      throw new Error("Fly plugin not initialized")
    }

    try {
      // Publish approval started event
      this.eventBus.publish(new SwapApprovalStartedEvent(quote))

      // Get target address from quote metadata
      const targetAddress = quote.transactionMetadata?.targetAddress as string | undefined
      if (!targetAddress) {
        throw new Error("Target address not found in quote metadata")
      }

      // Check if approval is needed
      if (currentAllowance >= quote.inputAmountRaw) {
        // Already approved, publish completion event
        this.eventBus.publish(
          new SwapApprovalCompletedEvent(quote, "0x0000000000000000000000000000000000000000000000000000000000000000"),
        )
        return null
      }

      // Calculate approval amount based on approval mode and contract risk level
      const approvalAmount = determineApprovalAmount(quote.inputAmountRaw, approvalMode, contractRiskLevel)

      // Encode approve(address spender, uint256 amount)
      // Function selector: approve(address,uint256) = 0x095ea7b3
      const spenderAddress = targetAddress.toLowerCase().replace("0x", "").padStart(64, "0")
      const amountHex = approvalAmount.toString(16).padStart(64, "0")
      const data = ("0x095ea7b3" + spenderAddress + amountHex) as `0x${string}`

      const approvalTransaction: NormalizedSwapTransaction = {
        to: quote.inputToken.address.toViemAddress(),
        data: data as `0x${string}`,
        value: 0n,
        gasLimit: BigInt(50000), // Standard ERC20 approval gas
      }

      return approvalTransaction
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error"
      if (this.eventBus) {
        this.eventBus.publish(new SwapErrorEvent(quote, errorMessage))
      }
      throw error
    }
  }

  async executeSwapTransaction(
    quote: NormalizedSwapQuote,
    userAddress: Address,
  ): Promise<NormalizedSwapTransaction | null> {
    // Build transaction (orchestrator handles event publishing)
    return this.buildTransaction(quote, {
      quote,
      userAddress,
    })
  }
}
